import React, { useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { ayudaApi } from "../../../services/api"
import { useMutation, useQuery, useQueryClient } from "react-query"
import Table from "../../common/Table"
import {
  ayudaResidentColumns,
  capitalize,
  statusStyles,
  computeDisbursementStats,
} from "../const/const"
import { BanknotesIcon } from "@heroicons/react/24/outline"
import ProgramCreation from "../modals/ProgramCreation"
import ActionsDropdown from "../action/ActionDropdown"
import AyudaDisbursementModal from "../modals/AyudaDisbursement"
import { useAuthStore } from "../../../stores/authStore"

export const AyudaDetailPage = () => {
  const { id } = useParams()
  const { state: programState } = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [selectedRows, setSelectedRows] = useState([])
  const [open, setOpen] = useState(false)
  const { user, isAdmin, isBarangayOfficial } = useAuthStore()

  const shouldHideButtons = isAdmin() || isBarangayOfficial()

  const {
    data: programData,
    isLoading: programLoading,
    error: programError,
  } = useQuery({
    queryKey: ["ayuda-program", id],
    queryFn: () => ayudaApi.getByAyudaId(id),
    enabled: !programState,
  })

  const program = programState ?? programData?.data

  const {
    data: residentsData,
    error: residentsError,
    isLoading: residentsLoading,
  } = useQuery({
    queryKey: ["ayuda-residents", id, currentPage, perPage],
    queryFn: async () => {
      const res = await ayudaApi.getByAyudaResidents(id, {
        page: currentPage,
        per_page: perPage,
      })
      return res.data
    },
    keepPreviousData: true,
    enabled: !!id,
  })

  const archiveMutation = useMutation((id) => ayudaApi.removeAyuda(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["ayuda-programs"])
      queryClient.removeQueries(["ayuda-program", id])
      navigate("/ayuda")
    },
  })
  const disburseMutation = useMutation(
    (payload) => ayudaApi.disburseAyuda(Number(id), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["ayuda-programs"])
        queryClient.invalidateQueries(["ayuda-residents"])

        queryClient.invalidateQueries(["ayuda-residents", id])
        queryClient.invalidateQueries(["ayuda-program", id])

        setSelectedRows([])
        setOpen(false)
        window.location.reload()
      },
    }
  )

  const handleArchive = () => {
    const confirmed = window.confirm(
      "Are you sure you want to archive this Ayuda program?"
    )

    if (!confirmed) return

    archiveMutation.mutate(id)
  }
  const handleDisburse = () => {
    if (!selectedRows.length) {
      alert("Please select at least one recipient.")
      return
    }

    const residentIds = tableData
      .filter((row) => selectedRows.includes(row.id))
      .map((row) => row.resident_id)

    if (!residentIds.length) {
      alert("No valid residents selected.")
      return
    }

    disburseMutation.mutate({
      resident_ids: residentIds,
    })
  }
  if (programLoading || residentsLoading) {
    return <div className='p-6 text-gray-500'>Loading program...</div>
  }

  if (programError || !program) {
    return (
      <div className='p-6'>
        <button onClick={() => navigate(-1)} className='text-blue-600'>
          ← Back
        </button>
        <p className='mt-4 text-gray-600'>Program not found.</p>
      </div>
    )
  }

  if (residentsError) {
    return (
      <div className='p-6'>
        <p className='text-red-600'>Failed to load recipients.</p>
      </div>
    )
  }

  const recipients = residentsData?.data?.residents ?? []
  const meta = residentsData?.data?.meta ?? {}
  const recipientsDetail = residentsData?.data?.ayuda_program ?? []

  // added
  const { total, released, pending, progress } =
    computeDisbursementStats(recipients)

  const tableData = recipients
    .map((item) => {
      const r = item.resident || {}

      return {
        id: item.id,
        resident_id: item.resident_id,
        name: [r.first_name, r.middle_name, r.last_name]
          .filter(Boolean)
          .join(" "),
        contact_number: r.contact_number ?? r.mobile_number ?? "-",
        province: r.province ?? "-",
        municipality: r.municipality ?? "-",
        barangay: r.barangay ?? "-",
        status: item.status,
        raw: item,
      }
    })
    .filter((row) => {
      if (!search) return true
      const keyword = search.toLowerCase()

      return (
        row.name.toLowerCase().includes(keyword) ||
        row.province.toLowerCase().includes(keyword) ||
        row.municipality.toLowerCase().includes(keyword) ||
        row.barangay.toLowerCase().includes(keyword)
      )
    })

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setCurrentPage(1)
  }

  const coverageDescription = () => {
    if (program.is_national) return "National"

    const coverage = program.coverage || {}
    const { region_name, province_name, municipality_name, barangay_name } =
      coverage

    if (region_name?.length && !province_name?.length) {
      return `${region_name.join(
        ", "
      )} (All Provinces, Municipalities & Barangays)`
    }

    if (province_name?.length && !municipality_name?.length) {
      return `  ${region_name?.join(", ")} –   ${province_name.join(
        ", "
      )} (All Municipalities & Barangays)`
    }

    if (municipality_name?.length && !barangay_name?.length) {
      return `  ${region_name?.join(", ")} –  ${province_name?.join(
        ", "
      )} –   ${municipality_name.join(", ")} (All Barangays)`
    }

    if (barangay_name?.length) {
      return `  ${region_name?.join(", ")} –    ${province_name?.join(
        ", "
      )} –   ${municipality_name?.join(", ")} –   ${barangay_name.join(", ")}`
    }

    return "All Regions, Provinces, Municipalities & Barangays"
  }

  const handlePageChange = (page, newPerPage) => {
    if (newPerPage && newPerPage !== perPage) {
      setPerPage(newPerPage)
      setCurrentPage(1)
    } else {
      setCurrentPage(page)
    }
  }

  const handleProgramUpdateSuccess = () => {
    setShowForm(false)
    queryClient.invalidateQueries(["ayuda-programs"])
    queryClient.invalidateQueries(["ayuda-residents"])
    queryClient.invalidateQueries(["ayuda-program", id])
    navigate("/ayuda")
  }

  return (
    <div className='max-w-7xl mx-auto '>
      <div className='flex   justify-between mb-2'>
        <div>
          <button
            onClick={() => navigate(-1)}
            className='text-blue-600 text-sm mb-2'
          >
            ← Back to Ayuda Programs
          </button>
          <h1 className='text-2xl font-bold text-gray-900'>{program.title}</h1>
          <p className='text-gray-600 mt-1'>{program.description}</p>
        </div>
        <div className='flex gap-2 items-end'>
          {!shouldHideButtons && (
            <button
              onClick={() => setOpen(true)}
              disabled={recipientsDetail?.status === "completed"}
              className='flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg
             hover:bg-blue-700 transition-all duration-200
             disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400'
            >
              <BanknotesIcon className='w-4 h-4' />
              <span className='text-[13px]'>Disbursement</span>
            </button>
          )}
          {!shouldHideButtons && (
            <ActionsDropdown
              setShowForm={setShowForm}
              handleArchive={handleArchive}
              archiveMutation={archiveMutation}
            />
          )}
        </div>
      </div>

      {showForm && (
        <ProgramCreation
          selectedAyuda={program}
          resetProgramForm={() => setShowForm(false)}
          onSuccess={handleProgramUpdateSuccess}
          initialSelectedResidents={tableData.map((r) => r.resident_id)}
        />
      )}

      {open && (
        <AyudaDisbursementModal
          open={open}
          onClose={() => setOpen(false)}
          tableData={tableData}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          handlePageChange={handlePageChange}
          meta={meta}
          currentPage={currentPage}
          perPage={perPage}
          program={program}
          onDisburse={handleDisburse}
          isDisbursing={disburseMutation.isLoading}
        />
      )}

      <div className='grid grid-cols-1 md:grid-cols-4 gap-2'>
        {/* <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Status</p>
          <span
            className={`px-2.5 py-1 text-xs font-medium rounded-full ${
              statusStyles[program.status] || "bg-gray-100 text-gray-600"
            }`}
          >
            {capitalize(program.status)}
          </span>
        </div> */}

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Status</p>

          <span
            className={`px-2.5 py-1 text-xs font-medium rounded-full ${
              statusStyles[recipientsDetail.status] ||
              "bg-gray-100 text-gray-600"
            }`}
          >
            {capitalize(recipientsDetail.status)}
          </span>

          {/* Progress Numbers */}
          <p className='mt-2 text-sm font-semibold text-gray-800'>
            {released}/{total}
          </p>

          <p className='text-xs text-gray-500'>{pending} pending</p>
        </div>

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Amount Allocated</p>
          <p className='font-medium mt-1'>
            {recipientsDetail.total_disbursed_amount}/
            {recipientsDetail.total_amount_allocated}{" "}
          </p>
        </div>

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>
            Amount{" "}
            {program.is_per_household === 1
              ? "Per Household"
              : "Per Individual"}
          </p>
          <p className='text-lg font-medium'>
            {/* {program.amount
              ? `₱${Number(program.amount).toLocaleString()}`
              : "—"} */}
            ₱{program.amount}
          </p>
        </div>
        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Beneficiary Type</p>
          <span className='font-medium'>
            {capitalize(
              program.is_per_household === 1
                ? "Per Household"
                : "Per Individual"
            )}
          </span>
        </div>

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Age Range</p>
          <p className='text-lg font-medium'>{capitalize(program.age)}</p>
          {/* {program.min_age || program.max_age
            ? `${program.min_age ?? "—"} – ${program.max_age ?? "—"}`
            : "All Ages"}{" "} */}
        </div>

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Civil Status</p>
          <p className='text-lg font-medium'>
            {Array.isArray(program.civil_status)
              ? program.civil_status.map(capitalize).join(", ")
              : capitalize(program.civil_status) || "All"}
          </p>
        </div>

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Monthly Income</p>
          <p className='text-lg font-medium'>
            {capitalize(program.monthly_income)}
          </p>
        </div>

        <div className='bg-white border rounded-lg p-3'>
          <p className='text-sm text-gray-500'>Vulnerable Sector</p>
          <p className='text-lg font-medium'>
            {Array.isArray(program.vulnerable_sector)
              ? program.vulnerable_sector.map(capitalize).join(", ")
              : capitalize(program.vulnerable_sector) || "All"}
          </p>
        </div>

        <div className='bg-white border rounded-lg p-3 md:col-span-4'>
          <p className='text-sm text-gray-500'>Coverage Area</p>
          <p className='font-medium mt-1'>{coverageDescription()}</p>
        </div>
      </div>

      <div className='bg-white border rounded-lg overflow-hidden mt-3'>
        <div className='p-3 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
          <h2 className='text-lg font-semibold'>
            Recipients({program?.recipients_count})
          </h2>

          <input
            type='text'
            placeholder='Search recipient...'
            value={search}
            onChange={handleSearch}
            className='w-full md:w-72 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none'
          />
        </div>

        <Table
          columns={ayudaResidentColumns}
          data={tableData}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          pagination={{
            currentPage,
            lastPage: meta.last_page,
            perPage,
            total: meta.total,
          }}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  )
}
